import './Today.scss'

import { useContext, useEffect, useState } from 'react'
import { Button, Container } from 'react-bootstrap'
import { useLocalStorage } from 'react-use'

import Membership from 'components/account/Membership'
import { app } from '../../../config'
import { getAuthenticatedUser } from 'components/auth'
import {
  saveUserEvent,
  EVENT_CATEGORY_TODAY,
  EVENT_ACTION_PAGEVIEW
} from 'components/common/api/UserEvents'
import { ROUTE_DAILY_REMBLE } from 'components/common/helpers/Paths'
import SubscriptionContext from 'components/common/contexts/SubscriptionContext'
import {
  getDailyRembleVideos,
  getWatchedDailyRembleVideos
} from 'components/common/api/DailyRemble'
import {
  isAndroid,
  isInApp,
  isiOS,
  openUrlInSafari,
  toggleTabBar
} from 'components/common/helpers/Mobile'
import { DailyRembleProvider } from 'components/common/contexts/DailyRembleContext'
import Share from '../Share'

import AlertDismissable from 'components/common/messaging/AlertDismissable'
import TodayAffirmation from '../TodayAffirmation/TodayAffirmation'
import JoyrideTour from 'components/joyrideTour/JoyrideTour'
import { useTourToday } from 'components/joyrideTour/pageMessages/useTourToday'
import {
  getUserPreferences,
  saveUserPreferences
} from 'components/common/api/Users'
import ScrollingCardsSkeleton from 'components/common/skeletons/ScrollingCardsSkeleton'

import vectorTop from '../../../assets/images/vectorTop.svg'
import vectorTopLeft from '../../../assets/images/vectorTopLeft.svg'
import TodaysProgram from '../TodaysProgram'
import TodayChat from 'components/chat/TodayChat/TodayChat'
import DailyRembleScrollingCards from 'components/daily-remble/DailyRembleScrollingCards/DailyRembleScrollingCards'
import MyGoalsWidget from 'components/my-goals/Widget'

/**
 * "Today" component. Used to display daily features and highlights to users
 */
export default function Today({ cache, setReturnPath, setHeaderTitle }) {
  const user = getAuthenticatedUser()

  // Get context data
  const { userSubscription } = useContext(SubscriptionContext)

  const [dailyRembleVideos, setDailyRembleVideos] = useState([])
  const [newDailyRembleVideos, setNewDailyRembleVideos] = useState([])
  const [activeDailyRembleVideo, setActiveDailyRembleVideo] = useState()
  const [userPreferences, setUserPreferences] = useState({})
  const [action, setAction] = useState({})

  const [hasBoardedLocal, setHasBoardedLocal] = useLocalStorage('hasBoarded')

  const readyToShowTour =
    user && // wait for user to be loaded to get the name
    Boolean(dailyRembleVideos.length)

  const { run, joyRideProps, joyRideSteps } = useTourToday(
    readyToShowTour,
    userPreferences
  )

  const hasOnboarded = Boolean(userPreferences?.account?.has_onboarded)

  const handleSetAction = action => setAction(action)

  // Add the tab bar back on mobile
  toggleTabBar('on')

  /**
   * Effect to get the daily remble videos
   */
  useEffect(() => {
    // Get Daily Remble Videos
    async function getDailyRembleVideosAsync() {
      // Check cache first
      const result = await cache.tryGetAsyncResult(
        ROUTE_DAILY_REMBLE,
        getDailyRembleVideos,
        this,
        { per_page: 100 }
      )
      // Get videos already watched by the user
      const watchedVideos = getWatchedDailyRembleVideos()

      // Filter out videos that have already been watched
      const filteredVideos =
        result && result.length
          ? result.filter(video => {
              return !watchedVideos.includes(video.id)
            })
          : []

      setNewDailyRembleVideos(filteredVideos)
      setDailyRembleVideos(result || [])
    }

    // Run it
    getDailyRembleVideosAsync()
  }, [cache])

  useEffect(() => {
    // Scroll to top
    window.scrollTo(0, 0)

    // Clear header and return path
    setReturnPath(null)
    setHeaderTitle(null)

    // Clear body class
    document.body.classList.remove('no-touch-action')

    // Log access
    saveUserEvent(EVENT_CATEGORY_TODAY, EVENT_ACTION_PAGEVIEW)
  }, [cache, setReturnPath, setHeaderTitle])

  /**
   * Effect to fetch the user's preferences
   */
  useEffect(() => {
    async function getUserPreferencesAsync() {
      const preferences = await getUserPreferences()

      setUserPreferences(preferences)
    }

    if (!hasBoardedLocal) {
      getUserPreferencesAsync()
    }
  }, [hasBoardedLocal])

  useEffect(() => {
    // If the user has finished or skipped the tour, save it to the user's preferences and on local storage
    if (action === 'stop' || action === 'skip') {
      saveBoarded()
      setHasBoardedLocal(true)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action])

  const saveBoarded = () => {
    const account = {
      has_onboarded: true
    }

    const updatedPreferences = { ...userPreferences, account }

    // Update the user's preferences
    saveUserPreferences(updatedPreferences)
  }

  return (
    <DailyRembleProvider
      value={{
        dailyRembleVideos: newDailyRembleVideos,
        activeDailyRembleVideo,
        setActiveDailyRembleVideo
      }}
    >
      <div id='daily-remble-fullscreen-player' className='hidden'></div>
      <div className='position-relative'>
        <img
          src={vectorTopLeft}
          alt=''
          className='vector position-absolute top-0'
          fetchpriority='low'
        />
        <img
          src={vectorTop}
          alt=''
          className='vector vector-top position-absolute'
          style={{ animationDelay: '0.5s' }}
          fetchpriority='low'
        />
      </div>
      <Container className='today-container'>
        {/* Today Chat component */}

        {/* If the user is a remble user, show the new chat component, otherwise the step counter */}
        <TodayChat userPreferences={userPreferences} />

        {/* Don't show if the user has an active plan.
            Since we're loading this one on top, let's not show it until we have subscription data */}
        {userSubscription && (
          <Membership
            variant='small'
            defaultShow={userSubscription?.plan_id === 0}
            subscription={userSubscription}
          />
        )}

        {/* Show Update notification if user is not on the latest version of iOS or Android */}
        <AlertDismissable
          dismissable={false}
          defaultShow={
            (isiOS() && !isiOS(app.ios.version)) ||
            (isAndroid() && !isAndroid(app.android.version))
          }
        >
          <h2 className='today-title'>New features available! 🎊 </h2>
          <p>
            Download the latest update on the Apple App Store to access them.
          </p>
          <Button
            variant='primary'
            onClick={() =>
              openUrlInSafari(
                'https://apps.apple.com/us/app/remble-relationship-health/id1597957847'
              )
            }
          >
            Update Now
          </Button>
        </AlertDismissable>

        {readyToShowTour && !hasOnboarded && (
          <JoyrideTour
            run={run}
            joyRideProps={joyRideProps}
            joyRideSteps={joyRideSteps}
            handleSetAction={handleSetAction}
          />
        )}
        <div id='js-tour-daily-remble'>
          {/* Don't show if we're still loading */}
          <h2 className='today-title'>Daily Remble</h2>
          {dailyRembleVideos.length > 0 ? (
            <DailyRembleScrollingCards
              dailyRembleVideos={newDailyRembleVideos}
              emptyText={`Wow${
                user?.display_name && ', ' + user.display_name
              }! You've watched all of the Daily Remble videos! Come back tomorrow for more. `}
            />
          ) : (
            <ScrollingCardsSkeleton className={'daily-remble-skeleton'} />
          )}
        </div>

        {/* My Goals */}
        <MyGoalsWidget />

        {/* For You Today */}
        <TodaysProgram />

        {/* Affirmation */}
        <TodayAffirmation cache={cache} />

        {/* Don't show if the user has an active plan */}
        <Membership defaultShow={!userSubscription?.active} />
        {/* Only show if the user is in the app */}
        {isInApp() && <Share />}
      </Container>
    </DailyRembleProvider>
  )
}
